
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CSOLoadingSpinner } from '@/components/ui/cso-loading-spinner';

export default function OrganizationJoin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [organization, setOrganization] = useState<any>(null);
  const [invitation, setInvitation] = useState<any>(null);
  const [isJoining, setIsJoining] = useState(false);

  const orgId = searchParams.get('org');
  const inviteId = searchParams.get('invite');

  useEffect(() => {
    if (!orgId || !inviteId) {
      toast({
        title: "Invalid Invitation",
        description: "The invitation link is invalid or expired.",
        variant: "destructive",
      });
      navigate('/');
      return;
    }

    fetchInvitationDetails();
  }, [orgId, inviteId]);

  const fetchInvitationDetails = async () => {
    try {
      setIsLoading(true);

      // Fetch organization details
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single();

      if (orgError) throw orgError;

      // Fetch invitation details
      const { data: inviteData, error: inviteError } = await supabase
        .from('organization_memberships')
        .select('*')
        .eq('id', inviteId)
        .eq('organization_id', orgId)
        .single();

      if (inviteError) throw inviteError;

      setOrganization(orgData);
      setInvitation(inviteData);
    } catch (error: any) {
      console.error('Error fetching invitation:', error);
      toast({
        title: "Error",
        description: "Failed to load invitation details.",
        variant: "destructive",
      });
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinOrganization = async () => {
    try {
      setIsJoining(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Redirect to auth with return URL
        navigate(`/auth?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
        return;
      }

      // Update the invitation with the current user
      const { error: updateError } = await supabase
        .from('organization_memberships')
        .update({
          user_id: user.id,
          joined_at: new Date().toISOString()
        })
        .eq('id', inviteId);

      if (updateError) throw updateError;

      toast({
        title: "Welcome!",
        description: `You've successfully joined ${organization.name}`,
      });

      // Redirect to organization dashboard or main dashboard
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error joining organization:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to join organization",
        variant: "destructive",
      });
    } finally {
      setIsJoining(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CSOLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Join Organization</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {organization && (
            <div className="text-center space-y-4">
              {organization.branding?.logo && (
                <img 
                  src={organization.branding.logo} 
                  alt={organization.name}
                  className="h-16 w-auto mx-auto"
                />
              )}
              
              <div>
                <h2 className="text-xl font-semibold">{organization.name}</h2>
                <p className="text-gray-600">
                  You've been invited to join this organization as a {invitation?.role}.
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Button 
              onClick={handleJoinOrganization}
              disabled={isJoining}
              className="w-full"
            >
              {isJoining ? "Joining..." : "Accept Invitation"}
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => navigate('/')}
              className="w-full"
            >
              Decline
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
